export enum TakeoverId {
  LUNAR_NEW_YEAR_TAKEOVER = 'lunarNewYearTakeover',
  VALENTINE_TAKEOVER = 'valentineTakeover',
  MARCH_TRAVEL_FRENZY_TAKEOVER = 'marchTravelFrenzyTakeover',
  TOUR_BEST_ASIA_PROMO_TAKEOVER = 'tourBestAsiaPromoTakeover',
  EASTER_TAKEOVER = 'easterTakeover',
  SOCIETY_TRIPLE_POINTS_TAKEOVER = 'societyTriplePointsTakeover',
  NEVER_ENDING_SUMMER_TAKEOVER = 'neverEndingSummerTakeover',
  LUX_PLUS_MONTH_TAKEOVER = 'luxPlusMonthTakeover',
  MALDIVES_CHARTER_TAKEOVER = 'maldivesCharterTakeover',
  DOUBlE_STATUS_CREDITS_TAKEOVER = 'doubleStatusCreditsTakeover',
}
/**
 * Type guard to check if a payload is an App.LabHeroCarouselPayload.
 * Validates that the payload is an object with a 'type' property equal to 'heroCarousel'.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabHeroCarouselPayload structure, false otherwise.
 */
export const isHeroCarouselPayload = (payload: unknown): payload is App.LabHeroCarouselPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'heroCarousel'
  )
}

/**
 * Type guard to check if a payload is an App.LabBannerMessagePayload.
 * Validates that the payload is an object with a 'type' property equal to 'bannerMessage'.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabBannerMessagePayload structure, false otherwise.
 */
export const isBannerMessagePayload = (payload: unknown): payload is App.LabBannerMessagePayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'bannerMessage'
  )
}

/**
 * Type guard to check if a payload is an App.LabOfferDisplayFlavorPayload.
 * Validates that the payload is an object with 'type' property equal to 'offerDisplay'
 * and a 'flavor' property that is a string.
 */
export const isOfferDisplayFlavorPayload = (payload: unknown): payload is App.LabOfferDisplayFlavorPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'offerDisplay' &&
    'flavor' in payload &&
    typeof (payload as { flavor?: unknown }).flavor === 'string'
  )
}

/**
 * Type guard to check if a payload is an App.LabSiteTakeoverPayload.
 * Validates that the payload is an object with 'type' property equal to 'siteTakeover'
 * and an 'id' property that matches a known TakeoverId from the registry.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabSiteTakeoverPayload structure and has a valid takeover ID, false otherwise.
 */
export const isLabSiteTakeoverPayload = (payload: unknown): payload is App.LabSiteTakeoverPayload => {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('type' in payload) ||
    !('id' in payload) ||
    (payload as { type?: unknown }).type !== 'siteTakeover' ||
    typeof (payload as { id?: unknown }).id !== 'string'
  ) {
    return false
  }

  return true
}

export const isLabCampaignTagPayload = (payload: unknown): payload is App.LabCampaignTagPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'campaignTag'
  )
}

export const isLabTakeoverBannerContentPayload = (payload: unknown): payload is App.LabTakeoverBannerContentPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'takeoverBanner'
  )
}

export const isLabTakeoverUSPPayload = (payload: unknown): payload is App.LabTakeoverUSPPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'takeoverUSP'
  )
}

export const isLabTakeoverModalPayload = (payload: unknown): payload is App.LabTakeoverModalPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'takeoverModal'
  )
}

export const isFeatureTogglePayload = (payload: unknown): payload is App.LabFeatureTogglePayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'featureToggle'
  )
}

/**
 * Type guard to check if a string is a valid TakeoverId.
 *
 * @param id - The string to check.
 * @returns True if the string is a valid TakeoverId, false otherwise.
 */
export const isTakeoverId = (id: string): id is TakeoverId => {
  return Object.values(TakeoverId).includes(id as TakeoverId)
}
